import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  NOTIFICATION_LEVEL_ERROR,
  NOTIFICATION_LEVEL_SUCCESS,
} from '@avtechsoftware/ra-javascript-utilities/src/types/NotificationLevels';
import { getNextNumericalName } from '@avtechsoftware/ra-javascript-utilities/src/utilities/validation/ObjectListUtils';
import NotificationTemplateFormInner
  from '../../../alert_actions/notification_templates/NotificationTemplateFormLayout/NotificationTemplateFormInner';
import {
  templateFieldNameMap,
} from '../../../alert_actions/notification_templates/NotificationTemplateFormLayout/NotificationTemplateForm/NotificationTemplateForm';
import useNotificationTemplate from '../../../hooks/useNotificationTemplate/useNotificationTemplate';
import {
  defaultClearEmailBody,
  defaultClearEmailSubject,
  defaultClearTextMessage,
  defaultTriggerEmailBody,
  defaultTriggerEmailSubject,
  defaultTriggerTextMessage,
} from '../../../../utility/AlertNotificationTemplateUtils';
import {
  createNotificationTemplate,
  fetchNotificationTemplates,
  selectNotificationTemplates,
  selectNotificationTemplatesCrudOperation,
  selectNotificationTemplatesCrudStatus,
  setNotificationTemplateCrudType,
} from '../../../../redux/slices/notificationTemplatesSlice/notificationTemplatesSlice';
import { pushSessionMessage } from '../../../../redux/slices/toastNotificationSlice/toastNotificationSlice';
import { CrudType } from '../../../../utility/types/CrudType';
import { CrudStatus } from '../../../../utility/types/CrudStatus';
import { selectSessionInformation } from '../../../../redux/slices/sessionSlice';
import { selectActionsData, updateStepData } from '../../../../redux/slices/alertFormSlice/alertFormSlice';

function NewNotificationTemplateModal(props) {
  const {
    show,
    handleClose,
    actionIdToApplyTemplate = null,
  } = props;

  const dispatch = useDispatch();
  const crudOperation = useSelector(selectNotificationTemplatesCrudOperation);
  const crudStatus = useSelector(selectNotificationTemplatesCrudStatus);
  const notificationTemplates = useSelector(selectNotificationTemplates);
  const { activeSubscription } = useSelector(selectSessionInformation);
  const { activeTierHasAdvancedAlerting } = activeSubscription;
  const { actions } = useSelector(selectActionsData);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitBtnText, setSubmitBtnText] = useState('Save');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [cursorFieldName, setCursorFieldName] = useState(
    templateFieldNameMap.TRIGGERED_EMAIL_SUBJECT,
  );
  const prevCrudStatusRef = useRef(null);

  const initialTemplate = {
    name: '',
    useDefaultEmailTemplate: true,
    useDefaultSmsTemplate: true,
    triggeredEmailSubject: defaultTriggerEmailSubject,
    triggeredEmailBody: defaultTriggerEmailBody,
    triggeredSmsBody: defaultTriggerTextMessage,
    clearedEmailSubject: defaultClearEmailSubject,
    clearedEmailBody: defaultClearEmailBody,
    clearedSmsBody: defaultClearTextMessage,
  };

  const {
    template,
    updateTemplate,
    resetTemplateByType,
  } = useNotificationTemplate({ initialTemplate, clone: false });

  const updateCursorProperties = (e) => {
    const { selectionStart, name } = e.target;
    setCursorPosition(selectionStart);
    setCursorFieldName(name);
  };

  const onFieldTextChange = (e) => {
    const { name, value } = e.target;
    updateCursorProperties(e);
    updateTemplate(name, value);
  };

  const insertKeywordAtCaret = (innerText) => {
    const { [cursorFieldName]: templateText } = template;
    const textBeforeCursorPosition = templateText.substring(0, cursorPosition);
    const textAfterCursorPosition = templateText
      .substring(cursorPosition, templateText.length);
    const newTemplateText = textBeforeCursorPosition + innerText + textAfterCursorPosition;
    updateTemplate(cursorFieldName, newTemplateText);
  };

  const handleSuccess = (message) => {
    dispatch(pushSessionMessage({
      level: NOTIFICATION_LEVEL_SUCCESS,
      title: 'Saved',
      body: message,
    }));
    dispatch(setNotificationTemplateCrudType(CrudType.READ));
    setIsSubmitting(false);
    setSubmitBtnText('Save');
    handleClose();
  };

  const handleFailure = (title, message) => {
    dispatch(fetchNotificationTemplates());
    dispatch(pushSessionMessage({
      level: NOTIFICATION_LEVEL_ERROR,
      title,
      body: message,
    }));
    setIsSubmitting(false);
    setSubmitBtnText('Save');
  };

  useEffect(() => {
    const prevCrudStatus = prevCrudStatusRef.current;
    prevCrudStatusRef.current = crudStatus;

    if (crudOperation === CrudType.CREATE && crudStatus !== prevCrudStatus) {
      if (crudStatus === CrudStatus.SUCCESS) {
        handleSuccess('Notification template saved successfully.');
      } else if (crudStatus === CrudStatus.ERROR) {
        handleFailure('Save Error', 'Error saving notification template.');
      }
    }
  }, [crudOperation, crudStatus]);

  useEffect(() => {
    if (show) {
      const newTemplateName = getNextNumericalName(
        notificationTemplates,
        'New Notification Template',
        'name',
      );
      resetTemplateByType(null); // Reset to initial state
      updateTemplate('name', newTemplateName);
      setIsSubmitting(false);
      setSubmitBtnText('Save');
    }
  }, [show]);

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitBtnText('Saving...');

    await dispatch(createNotificationTemplate({ notificationTemplate: template })).unwrap()
      .then((res) => {
        if (actionIdToApplyTemplate) {
          const updatedActions = actions.map((action) => {
            if (action.id !== actionIdToApplyTemplate) return action;

            return {
              ...action,
              templateId: res.id,
            };
          });
          dispatch(updateStepData({ step: 'DEFINE_ACTIONS', data: { actions: updatedActions } }));
        }
      });
    // Close is handled in success effect
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>New Notification Template</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {show && (
          <NotificationTemplateFormInner
            template={template}
            errorList={[]}
            onFieldTextChange={onFieldTextChange}
            updateCursorProperties={updateCursorProperties}
            insertKeywordAtCaret={insertKeywordAtCaret}
            updateTemplate={updateTemplate}
            resetTemplateByType={resetTemplateByType}
            activeTierHasAdvancedAlerting={activeTierHasAdvancedAlerting}
          />
        )}
      </Modal.Body>

      <Modal.Footer>
        <button type="button" className="btn btn-secondary" onClick={handleClose}>Cancel</button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSave}
          disabled={isSubmitting}
        >
          {submitBtnText}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

NewNotificationTemplateModal.defaultProps = {
  actionIdToApplyTemplate: null,
};

NewNotificationTemplateModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  actionIdToApplyTemplate: PropTypes.number,
};

export default NewNotificationTemplateModal;
